import { writable } from 'svelte/store';
import { spring } from 'svelte/motion';
import { onMount } from 'svelte';
import { browser } from '$app/environment';

// Store to track user's motion preferences
export const prefersReducedMotion = writable(false);

// Initialize motion preferences detection
export function initMotionPreferences() {
	if (!browser) return;

	onMount(() => {
		const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

		// Set initial value
		prefersReducedMotion.set(mediaQuery.matches);

		// Listen for changes
		const handleChange = (e: MediaQueryListEvent) => {
			prefersReducedMotion.set(e.matches);
		};

		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	});
}

// Helper function to conditionally apply transitions based on motion preferences
export function conditionalTransition<T>(
	reducedMotion: boolean,
	transition: T,
	fallback: Partial<T> | null = null
): T | Partial<T> | { duration: 0 } {
	if (reducedMotion) {
		return fallback || { duration: 0 };
	}
	return transition;
}

// Create animation-aware motion stores
export function createAnimatedScale(
	initialValue = 1,
	reducedMotion = false,
	config = { stiffness: 0.1, damping: 0.25 }
) {
	if (reducedMotion) {
		// Return a simple writable store for reduced motion
		return writable(initialValue);
	}

	// Use Svelte's spring motion for full animations
	return spring(initialValue, config);
}

// Performance optimization: disable complex animations on low-end devices
export function detectLowEndDevice(): boolean {
	if (!browser) return false;

	// Check for indicators of low-end device
	// `Navigator.connection` is part of the Network Information API which is not
	// in lib.dom.d.ts as of TS 5.6. Cast through `unknown` (not `any`) to a
	// minimal shape we read from.
	const connection = (navigator as unknown as { connection?: { effectiveType?: string } })
		.connection;
	const hardwareConcurrency = navigator.hardwareConcurrency || 4;

	const isLowEnd =
		hardwareConcurrency <= 2 ||
		(!!connection?.effectiveType && ['slow-2g', '2g'].includes(connection.effectiveType));

	return isLowEnd;
}

// Adaptive animation configuration
export function getAdaptiveAnimationConfig() {
	const isLowEnd = detectLowEndDevice();

	return {
		// Reduce duration on low-end devices
		durationMultiplier: isLowEnd ? 0.7 : 1,

		// Disable complex animations on low-end devices
		enableComplexAnimations: !isLowEnd,

		// Reduce stagger delays on low-end devices
		staggerMultiplier: isLowEnd ? 0.5 : 1,

		// Use simpler easing on low-end devices
		preferSimpleEasing: isLowEnd
	};
}

// Animation frame utilities for performance
export function createAnimationFrame() {
	let frameId: number;

	return {
		request: (callback: FrameRequestCallback) => {
			frameId = requestAnimationFrame(callback);
			return frameId;
		},
		cancel: () => {
			if (frameId) {
				cancelAnimationFrame(frameId);
			}
		}
	};
}

// Debounce utility for scroll-triggered animations
export function debounce<T extends (...args: unknown[]) => unknown>(
	func: T,
	wait: number,
	immediate = false
): (...args: Parameters<T>) => void {
	let timeout: ReturnType<typeof setTimeout> | null;

	return function executedFunction(...args: Parameters<T>) {
		const later = () => {
			timeout = null;
			if (!immediate) func(...args);
		};

		const callNow = immediate && !timeout;

		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(later, wait);

		if (callNow) func(...args);
	};
}
